import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Syringe, Stethoscope, Droplets, Heart, Star, ChevronRight, ArrowLeft, Clock, CalendarDays, DollarSign, CheckCircle2, Info, Loader2, Home, Video, Ticket, X } from "lucide-react";

const ICONS = { syringe: Syringe, stethoscope: Stethoscope, droplets: Droplets, heart: Heart };

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────
const brl = (n) => `R$ ${Number(n).toFixed(0)}`;
const fmtTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};
const fmtDay = (d) => d.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short" });
const isoDate = (d) => d.toISOString().slice(0, 10);

// ─────────────────────────────────────────────────────────
// Service details modal (opened when clicking a service card)
// ─────────────────────────────────────────────────────────
function ServiceDetailsModal({ service, isSelected, onToggle, onScheduleNow, onClose }) {
  if (!service) return null;
  const Icon = ICONS[service.icon] || Stethoscope;
  const benefits = service.benefits || [];
  const indications = service.indications || [];
  const hasContent = service.description || benefits.length > 0 || indications.length > 0;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
      data-testid="service-details-modal"
      role="dialog" aria-modal="true"
    >
      <div
        className="bg-card w-full sm:max-w-lg sm:rounded-sm rounded-t-2xl shadow-xl max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom-4 sm:zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle for mobile bottom sheet */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
        </div>

        <div className="p-6 pb-3 border-b border-border">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-sm grid place-items-center flex-none bg-accent/10 text-accent">
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="overline text-gold mb-1">{service.category}</div>
              <h3 className="brand-wordmark uppercase text-xl font-bold text-primary" data-testid="service-modal-name">
                {service.name}
              </h3>
              <div className="text-sm text-muted-foreground mt-1" data-testid="service-modal-duration-price">
                {service.duration_minutes} min · {brl(service.price)}
              </div>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm flex-none" data-testid="service-modal-close" aria-label="Fechar">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {hasContent ? (
            <>
              {service.description && (
                <div data-testid="service-modal-description">
                  <div className="overline text-gold mb-2 inline-flex items-center gap-1"><Info className="w-3 h-3" /> Sobre este serviço</div>
                  <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">{service.description}</p>
                </div>
              )}
              {benefits.length > 0 && (
                <div data-testid="service-modal-benefits">
                  <div className="overline text-gold mb-2 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Benefícios</div>
                  <ul className="space-y-1.5">
                    {benefits.map((b, i) => (
                      <li key={i} className="text-sm text-foreground flex items-start gap-2">
                        <span className="text-gold mt-1.5 w-1.5 h-1.5 rounded-full bg-gold flex-none" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {indications.length > 0 && (
                <div data-testid="service-modal-indications">
                  <div className="overline text-gold mb-2 inline-flex items-center gap-1"><Heart className="w-3 h-3" /> Indicações</div>
                  <ul className="space-y-1.5">
                    {indications.map((it, i) => (
                      <li key={i} className="text-sm text-foreground flex items-start gap-2">
                        <span className="text-primary mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-none" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <div className="py-6 text-center text-sm text-muted-foreground" data-testid="service-modal-empty">
              <Info className="w-6 h-6 mx-auto mb-2 text-muted-foreground/60" />
              Descrição em breve. Fale com um farmacêutico para saber mais.
            </div>
          )}
        </div>

        <div className="p-6 pt-0 sticky bottom-0 bg-card border-t border-border flex gap-2 flex-col sm:flex-row">
          <button
            onClick={() => { if (!isSelected) onToggle(service); onClose(); }}
            className="flex-1 rounded-sm border border-primary text-primary bg-card px-4 py-3 text-sm font-semibold hover:bg-primary/5 transition-colors"
            data-testid="service-modal-toggle"
          >
            {isSelected ? "Remover da seleção" : "Adicionar à seleção"}
          </button>
          <button
            onClick={() => { onScheduleNow(service); onClose(); }}
            className="flex-1 rounded-sm bg-primary text-primary-foreground px-4 py-3 text-sm font-semibold hover:bg-primary/90 transition-colors inline-flex items-center justify-center gap-1"
            data-testid="service-modal-schedule"
          >
            Agendar este serviço <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Step 1 · Multi-service selection (with cart)
// ─────────────────────────────────────────────────────────
export function ServiceStep({ selected, onToggle, onNext }) {
  const [services, setServices] = useState([]);
  const [detailService, setDetailService] = useState(null);
  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const grouped = services.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});

  const totalPrice = selected.reduce((a, b) => a + (b.price || 0), 0);
  const totalDuration = selected.reduce((a, b) => a + (b.duration_minutes || 0), 0);
  const isSelected = (id) => selected.some((x) => x.id === id);

  return (
    <div className="space-y-8" data-testid="step-service">
      <div>
        <div className="overline text-gold mb-3">Passo 1 de 4</div>
        <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">Escolha um ou mais serviços</h1>
        <p className="text-muted-foreground mt-2">Adicione todos os serviços que você quer nesta consulta. O valor e a duração são somados.</p>
      </div>

      {selected.length > 0 && (
        <div className="bg-card border border-primary/30 rounded-sm p-4 sticky top-2 z-10 shadow-sm" data-testid="service-cart">
          <div className="flex items-center justify-between mb-2">
            <div className="overline text-primary">Serviços selecionados ({selected.length})</div>
            <div className="text-sm font-heading font-bold text-primary" data-testid="cart-total">
              {brl(totalPrice)} · {totalDuration} min
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {selected.map((s) => (
              <button key={s.id} onClick={() => onToggle(s)}
                className="inline-flex items-center gap-1.5 rounded-sm bg-primary/5 border border-primary/20 px-2.5 py-1 text-xs hover:bg-primary/10 transition-colors"
                data-testid={`cart-item-${s.id}`}>
                {s.name} · {brl(s.price)}
                <span className="text-destructive font-bold">×</span>
              </button>
            ))}
          </div>
          <button onClick={onNext}
            className="mt-3 w-full rounded-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
            data-testid="services-continue-btn">
            Continuar com {selected.length} serviço{selected.length > 1 ? "s" : ""} →
          </button>
        </div>
      )}

      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat}>
          <div className="overline text-gold tracking-[0.22em] mb-3">{cat}</div>
          <div className="space-y-3">
            {items.map((s) => {
              const Icon = ICONS[s.icon] || Stethoscope;
              const chosen = isSelected(s.id);
              return (
                <div
                  key={s.id}
                  onClick={() => setDetailService(s)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetailService(s); } }}
                  role="button" tabIndex={0}
                  className={`w-full text-left border rounded-sm p-5 transition-colors group flex items-center gap-4 cursor-pointer ${chosen ? "border-primary bg-primary/5" : "bg-card border-border hover:border-primary hover:shadow-sm"}`}
                  data-testid={`service-${s.id}`}
                  aria-pressed={chosen}
                >
                  <div className={`w-11 h-11 rounded-sm grid place-items-center flex-none ${chosen ? "bg-primary text-primary-foreground" : "bg-accent/10 text-accent"}`}>
                    {chosen ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold">{s.name}</div>
                    <div className="text-sm text-muted-foreground">{s.duration_minutes} min · {brl(s.price)}</div>
                    {s.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-1">{s.description}</div>}
                    <div className="text-[10px] text-muted-foreground/70 mt-1 inline-flex items-center gap-1"><Info className="w-3 h-3" /> Toque no card para ver detalhes</div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onToggle(s); }}
                    className={`rounded-sm border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors flex-none ${chosen ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90" : "bg-card border-primary/40 text-primary hover:bg-primary/5"}`}
                    data-testid={`service-toggle-${s.id}`}
                    aria-label={chosen ? "Remover serviço" : "Adicionar serviço"}
                  >
                    {chosen ? "Remover" : "Adicionar"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <ServiceDetailsModal
        service={detailService}
        isSelected={detailService ? isSelected(detailService.id) : false}
        onToggle={onToggle}
        onScheduleNow={(s) => { if (!isSelected(s.id)) onToggle(s); setTimeout(() => onNext(), 0); }}
        onClose={() => setDetailService(null)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Step 2 · Professional selection (intersection across services)
// ─────────────────────────────────────────────────────────
export function ProfessionalStep({ services, onPick, onBack }) {
  const [prosPerService, setProsPerService] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all(services.map((s) => api.get(`/professionals?service_id=${s.id}`).then((r) => [s.id, r.data]).catch(() => [s.id, []])))
      .then((results) => {
        setProsPerService(Object.fromEntries(results));
        setLoading(false);
      });
  }, [services]);

  // Intersection: professionals who support ALL selected services
  const commonPros = (() => {
    const lists = Object.values(prosPerService);
    if (lists.length === 0) return [];
    const first = lists[0];
    return first.filter((p) => lists.every((lst) => lst.some((x) => x.id === p.id)));
  })();

  const totalPrice = services.reduce((a, b) => a + (b.price || 0), 0);
  const totalDuration = services.reduce((a, b) => a + (b.duration_minutes || 0), 0);

  return (
    <div className="space-y-6" data-testid="step-professional">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-service">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <div>
        <div className="overline text-gold mb-3">
          Passo 2 de 4 · {services.map((s) => s.name).join(" + ")} · {brl(totalPrice)} · {totalDuration} min
        </div>
        <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">Escolha o profissional</h1>
        {services.length > 1 && (
          <p className="text-xs text-muted-foreground mt-2">Mostrando somente profissionais que atendem todos os serviços selecionados.</p>
        )}
      </div>
      {loading ? (
        <div className="text-sm text-muted-foreground overline">Carregando…</div>
      ) : commonPros.length === 0 ? (
        <div className="bg-card border border-warning/30 rounded-sm p-6" data-testid="no-common-pros">
          <div className="font-heading font-semibold">Nenhum profissional atende todos os serviços selecionados.</div>
          <div className="text-sm text-muted-foreground mt-1">Remova algum serviço e tente novamente.</div>
          <button onClick={onBack} className="mt-4 inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors">
            Ajustar serviços
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {commonPros.map((p) => (
            <button
              key={p.id}
              onClick={() => onPick(p)}
              className="text-left bg-card border border-border rounded-sm p-5 hover:border-primary transition-colors flex items-center gap-4"
              data-testid={`professional-${p.id}`}
            >
              <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground grid place-items-center font-heading font-bold text-lg flex-none">
                {p.name.split(" ")[1]?.[0] || p.name[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold">{p.name}</div>
                <div className="text-xs text-muted-foreground">{p.specialty}</div>
                <div className="inline-flex items-center gap-1 mt-1 text-xs text-gold">
                  <Star className="w-3 h-3 fill-current" /> {p.rating}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Step 3 · Schedule (day strip + time slots)
// ─────────────────────────────────────────────────────────
export function ScheduleStep({ services, professional, onPick, onBack }) {
  const totalDuration = services.reduce((a, b) => a + (b.duration_minutes || 30), 0) || 30;
  const totalPrice = services.reduce((a, b) => a + (b.price || 0), 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    return d;
  });
  const [selectedDay, setSelectedDay] = useState(days[0]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chosen, setChosen] = useState(null);

  const loadSlots = async (day) => {
    setLoading(true); setChosen(null);
    try {
      const { data } = await api.get(`/professionals/${professional.id}/slots?date=${isoDate(day)}&duration_minutes=${totalDuration}`);
      setSlots(data);
    } catch (_) { setSlots([]); }
    setLoading(false);
  };

  useEffect(() => { loadSlots(selectedDay); }, [selectedDay]); // eslint-disable-line

  // Auto-refresh slots every 20s so admin changes reflect without manual reload
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/professionals/${professional.id}/slots?date=${isoDate(selectedDay)}&duration_minutes=${totalDuration}`);
        setSlots(data);
      } catch (_) { /* noop */ }
    }, 20000);
    return () => clearInterval(t);
  }, [selectedDay, professional.id, totalDuration]);

  const hasAvailable = slots.some((s) => s.available);

  return (
    <div className="space-y-6" data-testid="step-schedule">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-professional">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <div>
        <div className="overline text-gold mb-3">Passo 3 de 4 · {professional.name} · {services.length} serviço{services.length > 1 ? "s" : ""} ({totalDuration} min · {brl(totalPrice)})</div>
        <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">Escolha o horário</h1>
      </div>

      {/* Day strip */}
      <div className="flex gap-2 overflow-x-auto pb-2" data-testid="day-strip">
        {days.map((d, i) => {
          const isSel = d.toDateString() === selectedDay.toDateString();
          return (
            <button
              key={i}
              onClick={() => setSelectedDay(d)}
              className={`flex-none w-16 rounded-sm border p-2 text-center transition-colors ${isSel ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"}`}
              data-testid={`day-${isoDate(d)}`}
            >
              <div className={`text-[10px] uppercase tracking-wider ${isSel ? "opacity-80" : "text-muted-foreground"}`}>
                {d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "")}
              </div>
              <div className={`font-heading font-bold text-lg ${isSel ? "" : "text-primary"}`}>{d.getDate()}</div>
            </button>
          );
        })}
      </div>

      {/* Slots */}
      {loading ? (
        <div className="py-16 grid place-items-center text-muted-foreground text-sm">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : !hasAvailable ? (
        <div className="py-14 text-center" data-testid="no-slots-empty">
          <CalendarDays className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <div className="font-heading font-semibold">Sem horários disponíveis nesta data</div>
          <div className="text-sm text-muted-foreground mt-1">Escolha outra data ou tente o próximo dia disponível.</div>
        </div>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="slots-grid">
          {slots.map((s, i) => {
            const isSel = chosen?.start === s.start;
            const disabled = !s.available;
            return (
              <button
                key={i}
                onClick={() => !disabled && setChosen(s)}
                disabled={disabled}
                className={`px-3 py-2.5 rounded-sm border text-sm font-semibold transition-colors ${
                  isSel ? "bg-primary text-primary-foreground border-primary"
                  : disabled ? "bg-muted/50 border-border text-muted-foreground line-through cursor-not-allowed"
                  : "bg-card border-border hover:border-primary"
                }`}
                data-testid={`slot-${isoDate(new Date(s.start))}-${fmtTime(s.start)}`}
              >
                {fmtTime(s.start)}
              </button>
            );
          })}
        </div>
      )}

      <button
        onClick={() => chosen && onPick(chosen)}
        disabled={!chosen}
        className="w-full rounded-sm bg-primary text-primary-foreground px-4 py-3 text-sm font-semibold hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        data-testid="continue-to-confirm"
      >
        Continuar
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Step 4 · Confirmation
// ─────────────────────────────────────────────────────────
export function ConfirmStep({ services, professional, slot, onBack, onConfirmed, guestMode = false }) {
  const [mode, setMode] = useState("online");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [address, setAddress] = useState({ street: "", number: "", complement: "", neighborhood: "", city: "", zip: "" });
  // Via Rápida guest identification (only used when guestMode = true)
  const [guestName, setGuestName] = useState("");
  const [guestWhatsapp, setGuestWhatsapp] = useState("");

  const totalDuration = services.reduce((a, b) => a + (b.duration_minutes || 30), 0) || 30;
  const totalPrice = services.reduce((a, b) => a + (b.price || 0), 0);

  // Discount coupon state
  const [couponInput, setCouponInput] = useState("");
  const [couponInfo, setCouponInfo] = useState(null); // { code, discount_amount, final_price, discount_type, discount_value }
  const [couponError, setCouponError] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const applyCoupon = async () => {
    const code = (couponInput || "").trim();
    if (!code) return;
    setApplyingCoupon(true);
    setCouponError("");
    try {
      const { data } = await api.post("/coupons/validate", {
        code,
        service_ids: services.map((s) => s.id),
        subtotal: totalPrice,
      });
      setCouponInfo(data);
      toast.success(`Cupom ${data.code} aplicado.`);
    } catch (e) {
      setCouponInfo(null);
      setCouponError(e.response?.data?.detail || "Cupom inválido ou expirado");
    }
    setApplyingCoupon(false);
  };
  const removeCoupon = () => { setCouponInfo(null); setCouponInput(""); setCouponError(""); };

  const finalPrice = couponInfo ? couponInfo.final_price : totalPrice;

  const validateAddress = () => {
    if (mode !== "domiciliar") return null;
    for (const k of ["street", "number", "neighborhood", "city", "zip"]) {
      if (!(address[k] || "").trim()) return `Preencha ${k === "street" ? "rua" : k === "number" ? "número" : k === "neighborhood" ? "bairro" : k === "city" ? "cidade" : "CEP"}.`;
    }
    return null;
  };

  const submit = async () => {
    const err = validateAddress();
    if (err) { toast.error(err); return; }
    let guestDigits = "";
    if (guestMode) {
      if (!guestName.trim()) { toast.error("Digite seu nome completo."); return; }
      guestDigits = (guestWhatsapp || "").replace(/\D+/g, "");
      if (guestDigits.length < 10) { toast.error("Digite um WhatsApp válido (DDD + número)."); return; }
    }
    setSubmitting(true);
    try {
      const body = {
        appointment_type: mode,
        scheduled_at: slot.start,
        notes,
        service_ids: services.map((s) => s.id),
        professional_id: professional.id,
        address: mode === "domiciliar" ? address : null,
        coupon_code: couponInfo ? couponInfo.code : null,
      };
      if (guestMode) {
        body.guest_name = guestName.trim();
        body.guest_whatsapp = guestDigits;
        await api.post("/public/appointments", body);
      } else {
        await api.post("/appointments", body);
      }
      setDone(true);
      toast.success("Agendamento confirmado.");
    } catch (e) {
      const msg = e.response?.data?.detail || "Falha ao agendar.";
      toast.error(msg);
      if (/cupom/i.test(msg)) { setCouponInfo(null); setCouponError(msg); }
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="max-w-md mx-auto text-center py-16" data-testid="step-success">
        <div className="w-20 h-20 rounded-full bg-primary text-gold grid place-items-center mx-auto mb-6">
          <CheckCircle2 className="w-10 h-10" />
        </div>
        <h2 className="brand-wordmark text-2xl font-bold uppercase text-primary">Horário reservado!</h2>
        <p className="text-muted-foreground mt-3">Você receberá uma confirmação por notificação assim que o profissional validar o agendamento.</p>
        <button
          onClick={onConfirmed}
          className="mt-8 rounded-sm bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold hover:bg-primary/90 transition-colors"
          data-testid="return-to-appointments"
        >
          Voltar ao início
        </button>
      </div>
    );
  }

  const date = new Date(slot.start);

  return (
    <div className="space-y-6" data-testid="step-confirm">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-schedule">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <div>
        <div className="overline text-gold mb-3">Passo 4 de 4</div>
        <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">Revise os detalhes</h1>
      </div>

      <div className="bg-card border border-border rounded-sm divide-y divide-border" data-testid="confirm-summary">
        {services.map((s) => (
          <SumRow key={s.id} icon={Stethoscope} label={`${s.name} · ${s.duration_minutes} min · ${brl(s.price)}`} />
        ))}
        <SumRow icon={Star} label={`${professional.name} · ${professional.specialty}`} />
        <SumRow icon={CalendarDays} label={`${date.toLocaleDateString("pt-BR", { day: "2-digit", month: "long" })} · ${fmtTime(slot.start)}`} />
        <SumRow icon={Clock} label={`${totalDuration} min total`} />
      </div>

      <div className="bg-card border border-border rounded-sm p-4 space-y-3" data-testid="total-summary">
        {guestMode && (
          <div className="bg-primary/5 border border-primary/20 rounded-sm p-3 space-y-2 mb-2" data-testid="guest-identify-block">
            <div className="overline text-primary text-[10px] tracking-[0.22em]">Identificação</div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Nome completo *</label>
              <input
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Seu nome"
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                data-testid="guest-name-input"
                autoComplete="name"
              />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">WhatsApp *</label>
              <input
                value={guestWhatsapp}
                onChange={(e) => setGuestWhatsapp(e.target.value)}
                placeholder="(11) 99999-8888"
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                data-testid="guest-whatsapp-input"
                inputMode="tel"
                autoComplete="tel"
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Já tem conta? <a href="/login" className="text-primary underline">Entre</a> para usar seus dados salvos.
            </p>
          </div>
        )}
        {couponInfo ? (
          <>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="line-through text-muted-foreground" data-testid="original-price">{brl(totalPrice)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-success inline-flex items-center gap-1">
                <Ticket className="w-3.5 h-3.5" /> Cupom {couponInfo.code}
                {couponInfo.discount_type === "percent" ? ` · -${couponInfo.discount_value}%` : ""}
              </span>
              <span className="text-success" data-testid="discount-amount">− {brl(couponInfo.discount_amount)}</span>
            </div>
            <div className="border-t border-border pt-3 flex items-center justify-between">
              <span className="font-heading font-semibold">Total com desconto</span>
              <span className="brand-wordmark text-xl font-bold text-primary" data-testid="final-price">{brl(finalPrice)}</span>
            </div>
          </>
        ) : (
          <SumRow icon={DollarSign} label={`Total: ${brl(totalPrice)}`} bold />
        )}
      </div>

      {/* Discount coupon */}
      <div className="bg-card border border-border rounded-sm p-4" data-testid="coupon-panel">
        <div className="overline text-gold inline-flex items-center gap-2 mb-2"><Ticket className="w-3.5 h-3.5" /> Cupom de desconto</div>
        {couponInfo ? (
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm">
              <span className="font-mono font-semibold text-primary" data-testid="applied-coupon-code">{couponInfo.code}</span>
              <span className="text-muted-foreground ml-2">aplicado — economia de {brl(couponInfo.discount_amount)}</span>
            </div>
            <button
              type="button" onClick={removeCoupon}
              className="text-xs text-destructive hover:underline"
              data-testid="remove-coupon-btn"
            >
              Remover
            </button>
          </div>
        ) : (
          <div className="flex items-stretch gap-2">
            <input
              value={couponInput}
              onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
              placeholder="Digite o código"
              className="flex-1 px-3 py-2 rounded-sm border border-input bg-card text-sm font-mono uppercase"
              data-testid="coupon-input"
            />
            <button
              type="button" onClick={applyCoupon} disabled={applyingCoupon || !couponInput.trim()}
              className="rounded-sm bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
              data-testid="apply-coupon-btn"
            >
              {applyingCoupon ? "…" : "Aplicar"}
            </button>
          </div>
        )}
        {couponError && (
          <div className="mt-2 text-xs text-destructive" data-testid="coupon-error">{couponError}</div>
        )}
      </div>

      {/* Mode */}
      <div>
        <div className="overline text-muted-foreground mb-2">Modalidade</div>
        <div className="grid grid-cols-2 gap-3">
          {[{ v: "online", l: "Online", Icon: Video }, { v: "domiciliar", l: "Domiciliar", Icon: Home }].map(({ v, l, Icon }) => (
            <button
              key={v}
              onClick={() => setMode(v)}
              className={`flex items-center gap-3 rounded-sm border px-4 py-3 text-sm font-medium transition-colors ${mode === v ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-muted"}`}
              data-testid={`mode-${v}`}
            >
              <Icon className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>
      </div>

      {/* Home service address (conditional) */}
      {mode === "domiciliar" && (
        <div className="bg-card border border-border rounded-sm p-4 space-y-3" data-testid="address-form">
          <div className="overline text-muted-foreground">Endereço do atendimento domiciliar</div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-4">
              <label className="text-xs text-muted-foreground mb-1 block">Rua</label>
              <input value={address.street} onChange={(e) => setAddress({ ...address, street: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                data-testid="address-street-input" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground mb-1 block">Número</label>
              <input value={address.number} onChange={(e) => setAddress({ ...address, number: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                data-testid="address-number-input" />
            </div>
            <div className="md:col-span-3">
              <label className="text-xs text-muted-foreground mb-1 block">Complemento (opcional)</label>
              <input value={address.complement} onChange={(e) => setAddress({ ...address, complement: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                data-testid="address-complement-input" />
            </div>
            <div className="md:col-span-3">
              <label className="text-xs text-muted-foreground mb-1 block">Bairro</label>
              <input value={address.neighborhood} onChange={(e) => setAddress({ ...address, neighborhood: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                data-testid="address-neighborhood-input" />
            </div>
            <div className="md:col-span-4">
              <label className="text-xs text-muted-foreground mb-1 block">Cidade</label>
              <input value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                data-testid="address-city-input" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground mb-1 block">CEP</label>
              <input value={address.zip} onChange={(e) => setAddress({ ...address, zip: e.target.value })}
                placeholder="00000-000"
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                data-testid="address-zip-input" />
            </div>
          </div>
        </div>
      )}

      <div>
        <div className="overline text-muted-foreground mb-2">Observações (opcional)</div>
        <textarea
          value={notes} onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="Alguma informação para o profissional?"
          data-testid="confirm-notes"
        />
      </div>

      <div className="bg-gold/10 border border-gold/30 rounded-sm p-4 flex gap-3 items-start">
        <Info className="w-4 h-4 text-gold mt-0.5 flex-none" />
        <div className="text-sm text-muted-foreground">
          Cancelamentos com menos de 6h de antecedência podem estar sujeitos à política de cancelamento do centro.
        </div>
      </div>

      <button
        onClick={submit} disabled={submitting}
        className="w-full rounded-sm bg-primary text-primary-foreground px-4 py-3 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
        data-testid="confirm-submit-btn"
      >
        {submitting ? "Confirmando…" : "Confirmar agendamento"}
      </button>
    </div>
  );
}

function SumRow({ icon: Icon, label, bold }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3.5">
      <Icon className="w-4 h-4 text-primary flex-none" />
      <div className={`flex-1 text-sm ${bold ? "font-heading font-bold" : "font-medium"}`}>{label}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────
export default function AppointmentsPage() {
  const location = useLocation();
  const [step, setStep] = useState(location?.state?.openBooking ? "service" : "list");
  const [services, setServices] = useState([]);
  const [professional, setProfessional] = useState(null);
  const [slot, setSlot] = useState(null);
  const [items, setItems] = useState([]);

  const loadList = async () => {
    try { const { data } = await api.get("/appointments"); setItems(data); } catch (_) { /* noop */ }
  };
  useEffect(() => { loadList(); }, []);

  const reset = () => { setStep("list"); setServices([]); setProfessional(null); setSlot(null); loadList(); };

  const toggleService = (s) => {
    setServices((prev) => (prev.some((x) => x.id === s.id) ? prev.filter((x) => x.id !== s.id) : [...prev, s]));
  };

  if (step === "service") return (
    <ServiceStep
      selected={services}
      onToggle={toggleService}
      onNext={() => {
        if (services.length === 0) { toast.error("Selecione ao menos um serviço."); return; }
        setStep("professional");
      }}
    />
  );
  if (step === "professional") return <ProfessionalStep services={services} onBack={() => setStep("service")} onPick={(p) => { setProfessional(p); setStep("schedule"); }} />;
  if (step === "schedule") return <ScheduleStep services={services} professional={professional} onBack={() => setStep("professional")} onPick={(s) => { setSlot(s); setStep("confirm"); }} />;
  if (step === "confirm") return <ConfirmStep services={services} professional={professional} slot={slot} onBack={() => setStep("schedule")} onConfirmed={reset} />;

  // list view
  return (
    <div className="space-y-8" data-testid="appointments-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-gold mb-3">Agendamentos</div>
          <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">Suas consultas</h1>
        </div>
        <button
          onClick={() => setStep("service")}
          className="rounded-sm bg-primary text-primary-foreground px-5 py-3 text-sm font-semibold hover:bg-primary/90 transition-colors"
          data-testid="new-appointment-btn"
        >
          + Novo agendamento
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center">
          <CalendarDays className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <div className="font-heading font-semibold">Nenhuma consulta agendada</div>
          <div className="text-sm text-muted-foreground mt-1">Comece agendando um serviço.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((a) => (
            <div key={a.id} className="bg-card border border-border rounded-sm p-5" data-testid={`appt-item-${a.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-primary/10 text-primary grid place-items-center flex-none">
                  {a.appointment_type === "online" ? <Video className="w-5 h-5" /> : <Home className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold">
                    {a.services && a.services.length > 0
                      ? a.services.map((s) => s.name).join(" + ")
                      : (a.service_name || a.appointment_type)}
                  </div>
                  <div className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-1">
                    <CalendarDays className="w-3 h-3" /> {new Date(a.scheduled_at).toLocaleString("pt-BR")}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{a.professional}</div>
                  {a.service_price && <div className="text-xs text-gold mt-1 font-semibold">{brl(a.service_price)}{a.duration_minutes ? ` · ${a.duration_minutes} min` : ""}</div>}
                  {a.notes && <div className="text-sm mt-2 text-muted-foreground">{a.notes}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
